/**
 * Activities dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActivitiesDataTable } from '@/components/activities/ActivitiesDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateActivityView } from '@/components/activities/CreateActivityView';
import { EditActivityView } from '@/components/activities/EditActivityView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link ActivitiesSection}. */
interface ActivitiesSectionProps {
  layoutWidth?: ElementWidth;
  onSuccess?: () => void;
}

/** Dashboard widget: activities list with create/edit slide panel. */
export const ActivitiesSection: React.FC<ActivitiesSectionProps> = ({
  layoutWidth = 'full',
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('activities');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editActivityId, setEditActivityId] = React.useState<string | null>(null);

  const handleCreateActivity = () => {
    setCreateFormOpen(true);
    setEditActivityId(null);
    closeRow();
  };

  const handleEditActivity = (id: string) => {
    setEditActivityId(id);
    setCreateFormOpen(false);
    closeRow();
  };

  return (
    <>
      <ActivitiesDataTable
        showCreateButton={canCreate}
        isSection={true}
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditActivity={handleEditActivity}
        onCreateClick={handleCreateActivity}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateActivityView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['activities'] });
            onSuccess?.();
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editActivityId !== null} onClose={() => setEditActivityId(null)}>
        {editActivityId && (
          <EditActivityView
            key={editActivityId}
            activityId={editActivityId}
            onClose={() => setEditActivityId(null)}
            onSuccess={() => {
              setEditActivityId(null);
              void queryClient.invalidateQueries({ queryKey: ['activities'] });
              onSuccess?.();
            }}
          />
        )}
      </SlidePanel>
    </>
  );
};
